/**
 * useWorkflows.ts — list/create/rename/duplicate/delete/activate/deactivate
 * CRUD state for the Workflows tab, backed by services/workflow.service.ts.
 */

import { useCallback, useEffect, useState } from "react";
import { useAlert } from "../../functions/useAlert";
import {
  activateWorkflow,
  createWorkflow,
  deactivateWorkflow,
  deleteWorkflow,
  duplicateWorkflow,
  fetchWorkflows,
  generateWorkflow,
  updateWorkflow,
} from "../../services/workflow.service";
import type { WorkflowDefinition, WorkflowNode, WorkflowEdge } from "../workflow.types";
import { buildSeedWorkflows } from "./seedWorkflows";

export function useWorkflows() {
  const [setAlert] = useAlert();
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWorkflows();
      const list = res?.data || [];
      const requestedId = new URLSearchParams(window.location.search).get('workflowId');
      setWorkflows(list);
      setSelectedId((prev) =>
        requestedId && list.some((workflow) => workflow._id === requestedId)
          ? requestedId
          : prev && list.some((workflow) => workflow._id === prev)
            ? prev
            : list[0]?._id ?? null,
      );
    } catch (err: any) {
      setAlert(err?.message || "Failed to load workflows", "error");
    } finally {
      setLoading(false);
    }
  }, [setAlert]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyWorkflowUpdate = useCallback((updated: WorkflowDefinition) => {
    setWorkflows((prev) => prev.map((w) => (w._id === updated._id ? updated : w)));
  }, []);

  const createNewWorkflow = useCallback(
    async (payload: { name: string; description?: string; trigger: { type: string; config?: any } }) => {
      try {
        const res = await createWorkflow(payload);
        if (res?.data) {
          setWorkflows((prev) => [res.data, ...prev]);
          setSelectedId(res.data._id);
        }
        return res?.data ?? null;
      } catch (err: any) {
        setAlert(err?.message || "Failed to create workflow", "error");
        return null;
      }
    },
    [setAlert],
  );

  const generateNewWorkflow = useCallback(
    async (request: string) => {
      try {
        const res = await generateWorkflow(request);
        const generated = res?.data?.workflow;
        if (generated) {
          setWorkflows((prev) => [generated, ...prev]);
          setSelectedId(generated._id);
          const assumptions = res.data.assumptions || [];
          setAlert(
            assumptions.length ? `Workflow created. Review: ${assumptions.join(' ')}` : 'Workflow created — review and test it before activation.',
            assumptions.length ? 'warning' : 'success',
          );
        }
        return generated ?? null;
      } catch (err: any) {
        setAlert(err?.message || 'Failed to generate workflow', 'error');
        return null;
      }
    },
    [setAlert],
  );

  const renameWorkflow = useCallback(
    async (id: string, name: string) => {
      try {
        const res = await updateWorkflow(id, { name });
        if (res?.data) applyWorkflowUpdate(res.data);
      } catch (err: any) {
        setAlert(err?.message || "Failed to rename workflow", "error");
      }
    },
    [applyWorkflowUpdate, setAlert],
  );

  const updateDescription = useCallback(
    async (id: string, description: string) => {
      try {
        const res = await updateWorkflow(id, { description });
        if (res?.data) applyWorkflowUpdate(res.data);
      } catch (err: any) {
        setAlert(err?.message || "Failed to update the description", "error");
      }
    },
    [applyWorkflowUpdate, setAlert],
  );

  const updateTriggerConfig = useCallback(
    async (id: string, config: Record<string, unknown>) => {
      const workflow = workflows.find((w) => w._id === id);
      if (!workflow) return;
      try {
        const res = await updateWorkflow(id, { trigger: { type: workflow.trigger.type, config } });
        if (res?.data) applyWorkflowUpdate(res.data);
      } catch (err: any) {
        setAlert(err?.message || "Failed to update the trigger", "error");
      }
    },
    [workflows, applyWorkflowUpdate, setAlert],
  );

  const duplicateWorkflowEntry = useCallback(
    async (id: string) => {
      try {
        const res = await duplicateWorkflow(id);
        if (res?.data) {
          setWorkflows((prev) => [res.data, ...prev]);
          setAlert("Workflow duplicated", "success");
        }
      } catch (err: any) {
        setAlert(err?.message || "Failed to duplicate workflow", "error");
      }
    },
    [setAlert],
  );

  const deleteWorkflowEntry = useCallback(
    async (id: string) => {
      try {
        await deleteWorkflow(id);
        setWorkflows((prev) => prev.filter((w) => w._id !== id));
        setSelectedId((prev) => (prev === id ? null : prev));
      } catch (err: any) {
        setAlert(err?.message || "Failed to delete workflow", "error");
      }
    },
    [setAlert],
  );

  const activateWorkflowEntry = useCallback(
    async (id: string) => {
      try {
        const res = await activateWorkflow(id);
        if (res?.data) applyWorkflowUpdate(res.data);
        return res?.data ?? null;
      } catch (err: any) {
        setAlert(err?.message || "Failed to activate workflow", "error");
        return null;
      }
    },
    [applyWorkflowUpdate, setAlert],
  );

  const deactivateWorkflowEntry = useCallback(
    async (id: string) => {
      try {
        const res = await deactivateWorkflow(id);
        if (res?.data) applyWorkflowUpdate(res.data);
      } catch (err: any) {
        setAlert(err?.message || "Failed to deactivate workflow", "error");
      }
    },
    [applyWorkflowUpdate, setAlert],
  );

  const loadExampleWorkflows = useCallback(async () => {
    setIsSeeding(true);
    try {
      const seeds = buildSeedWorkflows();
      for (const seed of seeds) {
        const created = await createWorkflow({
          name: seed.name,
          description: seed.description,
          trigger: seed.trigger,
        });
        if (created?.data?._id) {
          await updateWorkflow(created.data._id, {
            nodes: seed.nodes as WorkflowNode[],
            edges: seed.edges as WorkflowEdge[],
          });
        }
      }
      await refresh();
    } catch (err: any) {
      setAlert(err?.message || "Failed to load example workflows", "error");
    } finally {
      setIsSeeding(false);
    }
  }, [refresh, setAlert]);

  const selectedWorkflow = workflows.find((w) => w._id === selectedId) || null;

  return {
    workflows,
    loading,
    selectedId,
    setSelectedId,
    selectedWorkflow,
    refresh,
    applyWorkflowUpdate,
    createNewWorkflow,
    generateNewWorkflow,
    renameWorkflow,
    updateDescription,
    updateTriggerConfig,
    duplicateWorkflowEntry,
    deleteWorkflowEntry,
    activateWorkflowEntry,
    deactivateWorkflowEntry,
    loadExampleWorkflows,
    isSeeding,
  };
}
